import { useEffect, useState, useMemo, useRef } from 'react'
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from "react-hook-form";
import DatePicker from "react-datepicker";
import Calendar2LineIcon from 'remixicon-react/Calendar2LineIcon';
import Modal from '../../../components/utils/Model';
import Button from '../../../components/utils/Button';
import dailyPlanRequestActions from '../../../redux/dailyPlanRequest/actions'
import Alert from '../../../components/utils/Alert';
import authActions from '../../../redux/auth/actions';
import { isAdmin, isAdminAgent, twoDigitDecimal } from '../../../helpers/commonFunction';
import useSocket from '../../../hooks/useSocket';

const { resetDailyPlan, reduceDailyPlanRequest, getDailyPlanRequestList } = dailyPlanRequestActions;

const DailyPlanReduce = ({ row, modal, close, search }) => {
    const dispatch = useDispatch();
    const { getUserDataByToken } = authActions;
    const {
        reduceDailyPlanRequestLoading,
        reduceDailyPlanRequestError,
        reduceDailyPlanRequestMessage
    } = useSelector((state) => state.dailyPlanRequest);
    const { tokenData } = useSelector((state) => state.auth);
    const [showAlert, setShowAlert] = useState(false);
    const [alertType, setAlertType] = useState('');
    const [alertText, setAlertText] = useState('');
    const [fromDate, setFromDate] = useState(moment(row.RequestedToDate).add(1, 'days').toDate());
    const [updatedDate, setUpdatedDate] = useState(moment(row.RequestedToDate).subtract(1, 'days').toDate());

    const { register, handleSubmit, formState: { errors } } = useForm({ defaultValues: {} });

    useEffect(() => {
        dispatch(resetDailyPlan());
        setFromDate(moment(row.RequestedToDate).add(1, 'days').toDate());
    }, []);

    useSocket('reduce daily plan request', (data) => {
        if (isAdmin(tokenData) || isAdminAgent(tokenData)) {
            let socketData = JSON.parse(data);
            if (socketData.DailyPlanRequestID == row.ID) {
                dispatch(getDailyPlanRequestList(search));
                close();
            }

        }
    });

    useEffect(() => {
        if (reduceDailyPlanRequestError !== null && reduceDailyPlanRequestError !== undefined) {
            displayAlert(reduceDailyPlanRequestError, 'alert-danger');
        }
        if (reduceDailyPlanRequestMessage !== null && reduceDailyPlanRequestMessage !== undefined) {
            close();
        }
    }, [reduceDailyPlanRequestError, reduceDailyPlanRequestMessage]);

    useEffect(() => {
        dispatch(getUserDataByToken());
    }, [])

    const onSubmit = (data) => {
        data.DailyPlanRequestID = row.ID;
        data.reducedDays = reducedDays;
        dispatch(reduceDailyPlanRequest(data, search));
    }

    const displayAlert = (text, type) => {
        setShowAlert(true);
        setAlertText(text);
        setAlertType(type);
    };

    const alertCancel = () => {
        setShowAlert(false);
        setAlertText('');
        setAlertType('');
        dispatch(resetDailyPlan());
    }

    const datePickerRef = useRef(null);

    const openDatePicker = () => {
        datePickerRef.current.setOpen(true);
    };

    const reducedDays = useMemo(() => {
        const date1 = moment(updatedDate).startOf("day");
        const date2 = moment(row.RequestedToDate).startOf("day");

        const diffInDays = date2.diff(date1, 'days');
        return diffInDays;
    }, [updatedDate]);

    return (
        <Modal
            showModal={modal}
            title={`REDUCE DAILY PLAN REQUEST: ${row.TransactionID}`}
            onHide={() => close()}
            width={700}
            showFooter={false}
        >
            {showAlert && <Alert type={alertType} text={alertText} closeBtn={alertCancel} />}
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="form-row">
                    <div className="col-lg-4 mb-3 d-flex flex-column">
                        <label>SIM Type</label>
                        <p>{row.SimTypeValue}</p>
                    </div>

                    <div className="col-lg-4 mb-3 d-flex flex-column">
                        <label>IMEI</label>
                        <p>{row.IMEI}</p>
                    </div>

                    <div className="col-lg-4 mb-3 d-flex flex-column">
                        <label>Zip Code</label>
                        <p>{row.AreaCode}</p>
                    </div>

                    <div className="col-lg-4 mb-3 d-flex flex-column">
                        <label>Total Days</label>
                        <p>{row.RequestedDays} Days</p>
                    </div>
                    <div className="col-lg-4 mb-3">
                        <label>SIM Number</label>
                        <p className='wrap-line-vishal'>{row.SerialNumber}</p>
                    </div>

                    <div className="col-lg-4 mb-3 d-flex flex-column">
                        <label>Customer Email</label>
                        <p className='wrap-line-vishal'>{row.CustomerEmailID}</p>
                    </div>

                    <div className="col-lg-4 mb-3 d-flex flex-column">
                        <label>From Date</label>
                        <Calendar2LineIcon
                            size={'1.5rem'}
                            style={{ cursor: 'pointer', position: 'absolute', top: '36px', right: '12px', zIndex: 1 }} />
                        <DatePicker
                            selected={new Date(row.RequestedDtTm)}
                            className="form-control"
                            onChange={(date) => setFromDate(date)}
                            dateFormat="yyyy-MM-dd"
                            showMonthDropdown
                            showYearDropdown
                            onKeyDown={(e) => {
                                e.preventDefault();
                            }}
                            value={new Date(row.RequestedDtTm)}
                            disabled
                        />
                    </div>

                    <div className="col-lg-4 mb-3 d-flex flex-column">
                        <label>To Date</label>
                        <Calendar2LineIcon
                            size={'1.5rem'}
                            style={{ cursor: 'pointer', position: 'absolute', top: '36px', right: '12px', zIndex: 1 }} />
                        <DatePicker
                            selected={new Date(row.RequestedToDate)}
                            className="form-control"
                            onChange={(date) => setFromDate(date)}
                            dateFormat="yyyy-MM-dd"
                            showMonthDropdown
                            showYearDropdown
                            onKeyDown={(e) => {
                                e.preventDefault();
                            }}
                            value={new Date(row.RequestedToDate)}
                            disabled
                        />
                    </div>

                    <div className="col-md-4 mb-3 d-flex flex-column">
                        <label>Updated Date</label>
                        <Calendar2LineIcon
                            onClick={() => openDatePicker()}
                            size={'1.5rem'}
                            style={{ cursor: 'pointer', position: 'absolute', top: '36px', right: '12px', zIndex: 1 }} />
                        <DatePicker
                            ref={datePickerRef}
                            selected={updatedDate}
                            className="form-control"
                            onChange={(date) => setUpdatedDate(date)}
                            dateFormat="yyyy-MM-dd"
                            showMonthDropdown
                            showYearDropdown
                            onKeyDown={(e) => {
                                e.preventDefault();
                            }}
                            value={updatedDate}
                            minDate={moment(row.RequestedDtTm).add(3, 'days').toDate()}
                            maxDate={moment(row.RequestedToDate).subtract(1, 'days').toDate()}
                        />
                        {errors.remark && (<div className="invalid-feedback">{errors.remark.message}</div>)}
                    </div>
                </div>

                <div className='d-flex justify-content-around bg-success ' style={{ padding: "8px", borderRadius: "4px" }}>
                    <h5 className='font-weight-bold text-white px-lg-3 px-1'> Daily Rate : ${twoDigitDecimal(row.DailyPlanRequestChargedAmount)} </h5>
                    <h5 className='font-weight-bold text-white px-lg-3 px-1'>Reduce For : {reducedDays > 1 ? `${reducedDays} Days` : `${reducedDays} Day`}</h5>
                    <h5 className='font-weight-bold text-white px-lg-3 px-1'> Refunded Amount : ${twoDigitDecimal(row.DailyPlanRequestChargedAmount * reducedDays)} </h5>
                </div>

                <hr />
                <div className='text-right row'>
                    <div className="col-md-12">
                        <button type="button" className="btn btn-secondary mr-2" onClick={() => close()}>Close</button>
                        <Button title="Submit" loading={reduceDailyPlanRequestLoading} buttonClass="btn btn-primary" buttonType='submit' />
                    </div>
                </div>
            </form>
        </Modal>
    )
}

export default DailyPlanReduce;